// Tarifario por tipo de habitación: calendario mensual con precio/día + panel
// "Aplicar temporada" (hotel.tarifas_aplicar). Hotel → Tarifas.
// La escritura (tarifas_aplicar) exige nivel de acceso 5 (hotel._exigir_admin).
import { ReactNode, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { usePermissions } from '@/lib/hooks/usePermissions';
import { Campo, Switch, fieldCls, money, hotelRpc, hotelTabla, QK, TarifaDia, TipoHabitacion } from './shared';

const pad2 = (n: number) => String(n).padStart(2, '0');
const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
// ISO: 1=lunes…7=domingo (misma convención que tarifas_aplicar._dias)
const DIAS_ISO: { iso: number; label: string }[] = [
  { iso: 1, label: 'L' }, { iso: 2, label: 'M' }, { iso: 3, label: 'X' }, { iso: 4, label: 'J' },
  { iso: 5, label: 'V' }, { iso: 6, label: 'S' }, { iso: 7, label: 'D' },
];
const isoDow = (iso: string) => { const d = new Date(`${iso}T12:00:00`).getDay(); return d === 0 ? 7 : d; };

export function TarifasHotel() {
  const qc = useQueryClient();
  const { isAdmin } = usePermissions();
  const puedeEditar = isAdmin();

  const { data: tipos = [] } = useQuery<TipoHabitacion[]>({
    queryKey: [QK, 'tipos'], queryFn: () => hotelTabla<TipoHabitacion>('tipos_habitacion', 'order=orden,codigo'),
  });
  const [tipoId, setTipoId] = useState('');
  const tipo = tipos.find((t) => t.id === tipoId) ?? null;
  // Selecciona el primer tipo activo apenas carguen (sin useEffect: se recalcula cada render hasta que haya selección)
  if (!tipoId && tipos.length > 0) setTipoId(tipos[0].id);

  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const desdeISO = `${cursor.y}-${pad2(cursor.m + 1)}-01`;
  const diasEnMes = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const hastaISO = `${cursor.y}-${pad2(cursor.m + 1)}-${pad2(diasEnMes)}`;

  const { data: filas = [], isLoading } = useQuery<TarifaDia[]>({
    queryKey: [QK, 'tarifas', tipoId, desdeISO],
    queryFn: () => hotelTabla<TarifaDia>('tarifas', `tipo_id=eq.${tipoId}&fecha=gte.${desdeISO}&fecha=lte.${hastaISO}&order=fecha`),
    enabled: !!tipoId,
  });
  const porFecha = useMemo(() => {
    const m = new Map<string, TarifaDia>();
    filas.forEach((f) => m.set(f.fecha.slice(0, 10), f));
    return m;
  }, [filas]);

  const celdas = useMemo(() => {
    const leading = isoDow(desdeISO) - 1;
    const arr: (string | null)[] = Array.from({ length: leading }, () => null);
    for (let d = 1; d <= diasEnMes; d++) arr.push(`${cursor.y}-${pad2(cursor.m + 1)}-${pad2(d)}`);
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [desdeISO, diasEnMes, cursor]);

  const mesAnterior = () => setCursor((c) => (c.m === 0 ? { y: c.y - 1, m: 11 } : { y: c.y, m: c.m - 1 }));
  const mesSiguiente = () => setCursor((c) => (c.m === 11 ? { y: c.y + 1, m: 0 } : { y: c.y, m: c.m + 1 }));

  const refrescar = () => void qc.invalidateQueries({ queryKey: [QK] });

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><CalendarDays className="w-5 h-5" /> Tarifario</h1>
        <Campo label="Tipo de habitación" className="min-w-[260px]">
          <select className={fieldCls} value={tipoId} onChange={(e) => setTipoId(e.target.value)}>
            {tipos.map((t) => <option key={t.id} value={t.id}>{t.codigo} · {t.nombre} (base {money(t.tarifa_base)})</option>)}
          </select>
        </Campo>
      </div>

      {!puedeEditar && (
        <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
          Solo un administrador (nivel de acceso 5) puede aplicar temporadas de tarifa. Aquí puedes consultar el calendario.
        </p>
      )}

      {!tipo ? (
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">
          {tipos.length === 0 ? 'Primero crea un tipo de habitación en Catálogo.' : 'Elige un tipo de habitación.'}
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_360px]">
          <Card>
            <CardHeader className="pb-2 flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={mesAnterior}><ChevronLeft className="w-4 h-4" /></Button>
                {MESES[cursor.m]} {cursor.y}
                <Button size="sm" variant="outline" onClick={mesSiguiente}><ChevronRight className="w-4 h-4" /></Button>
              </CardTitle>
              <span className="text-xs text-muted-foreground">Tarifa base del tipo: <strong>{money(tipo.tarifa_base)}</strong> (gris = sin tarifa especial ese día)</span>
            </CardHeader>
            <CardContent>
              {isLoading ? <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div> : (
                <>
                  <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold text-muted-foreground mb-1">
                    {DIAS_ISO.map((d) => <div key={d.iso}>{d.label}</div>)}
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {celdas.map((fecha, i) => {
                      if (!fecha) return <div key={`b${i}`} className="aspect-square rounded border border-dashed border-transparent" />;
                      const dia = fecha.slice(8, 10);
                      const f = porFecha.get(fecha);
                      const precio = f?.precio ?? tipo.tarifa_base;
                      const tienePropia = !!f;
                      return (
                        <div key={fecha}
                          className={`flex aspect-square flex-col items-center justify-center rounded border p-1 text-center ${
                            f?.cerrada ? 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/20'
                              : tienePropia ? 'border-sky-300 bg-sky-50 dark:border-sky-700 dark:bg-sky-900/20'
                              : 'border-border bg-muted/30'}`}>
                          <span className="text-[11px] text-muted-foreground">{dia}</span>
                          <span className={`text-xs font-semibold tabular-nums ${tienePropia ? '' : 'text-muted-foreground'}`}>{money(precio)}</span>
                          <div className="flex flex-wrap items-center justify-center gap-0.5 text-[9px] leading-none">
                            {f?.cerrada && <span className="rounded bg-red-600 px-1 py-0.5 font-semibold text-white">CERRADA</span>}
                            {!!f?.min_estadia && <span className="rounded bg-muted px-1 py-0.5">mín {f.min_estadia}n</span>}
                            {!!f?.max_estadia && <span className="rounded bg-muted px-1 py-0.5">máx {f.max_estadia}n</span>}
                            {f?.cta && <span className="rounded bg-amber-200 px-1 py-0.5 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200" title="Cerrado a llegadas (CTA)">CTA</span>}
                            {f?.ctd && <span className="rounded bg-amber-200 px-1 py-0.5 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200" title="Cerrado a salidas (CTD)">CTD</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {puedeEditar && <AplicarTemporada tipoId={tipo.id} onAplicado={refrescar} />}
        </div>
      )}
    </div>
  );
}

function AplicarTemporada({ tipoId, onAplicado }: { tipoId: string; onAplicado: () => void }) {
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [dias, setDias] = useState<Record<number, boolean>>(() => Object.fromEntries(DIAS_ISO.map((d) => [d.iso, true])));
  const [cPrecio, setCPrecio] = useState(true); const [precio, setPrecio] = useState(0);
  const [cMin, setCMin] = useState(false); const [minEst, setMinEst] = useState('');
  const [cMax, setCMax] = useState(false); const [maxEst, setMaxEst] = useState('');
  const [cCta, setCCta] = useState(false); const [cta, setCta] = useState(false);
  const [cCtd, setCCtd] = useState(false); const [ctd, setCtd] = useState(false);
  const [cCerrada, setCCerrada] = useState(false); const [cerrada, setCerrada] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const algoSeleccionado = cPrecio || cMin || cMax || cCta || cCtd || cCerrada;

  const aplicar = async () => {
    if (!desde || !hasta) { toast.error('Indica el rango de fechas'); return; }
    if (hasta < desde) { toast.error('«Hasta» no puede ser antes que «Desde»'); return; }
    if (!algoSeleccionado) { toast.error('Marca «cambiar» en al menos un campo'); return; }
    const todosLosDias = DIAS_ISO.every((d) => dias[d.iso]);
    const _dias = todosLosDias ? null : DIAS_ISO.filter((d) => dias[d.iso]).map((d) => d.iso);
    if (!todosLosDias && _dias!.length === 0) { toast.error('Selecciona al menos un día de la semana'); return; }
    const _d: Record<string, unknown> = {};
    if (cPrecio) _d.precio = precio;
    if (cMin) _d.min_estadia = minEst === '' ? null : Number(minEst);
    if (cMax) _d.max_estadia = maxEst === '' ? null : Number(maxEst);
    if (cCta) _d.cta = cta;
    if (cCtd) _d.ctd = ctd;
    if (cCerrada) _d.cerrada = cerrada;
    setEnviando(true);
    try {
      const r = await hotelRpc<{ ok: boolean; fechas: number }>('tarifas_aplicar', { _tipo: tipoId, _desde: desde, _hasta: hasta, _dias, _d });
      toast.success(`Se actualizaron ${r.fechas} fecha(s)`);
      onAplicado();
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setEnviando(false); }
  };

  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">Aplicar temporada</CardTitle></CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="grid grid-cols-2 gap-2">
          <Campo label="Desde *"><input type="date" className={fieldCls} value={desde} onChange={(e) => setDesde(e.target.value)} /></Campo>
          <Campo label="Hasta *"><input type="date" className={fieldCls} value={hasta} onChange={(e) => setHasta(e.target.value)} /></Campo>
        </div>
        <Campo label="Días de la semana">
          <div className="flex gap-1">
            {DIAS_ISO.map((d) => (
              <button key={d.iso} type="button" onClick={() => setDias((s) => ({ ...s, [d.iso]: !s[d.iso] }))}
                className={`h-8 w-8 rounded text-xs font-semibold ${dias[d.iso] ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                {d.label}
              </button>
            ))}
          </div>
        </Campo>

        <div className="space-y-2 border-t pt-2">
          <p className="text-xs text-muted-foreground">Marca «cambiar» en cada campo que quieras tocar; lo que no marques no se modifica.</p>
          <FilaCampo marcar={cPrecio} onMarcar={setCPrecio} titulo="Precio por noche">
            <input type="number" min={0} step="0.01" disabled={!cPrecio} className={fieldCls} value={precio} onChange={(e) => setPrecio(Number(e.target.value) || 0)} />
          </FilaCampo>
          <FilaCampo marcar={cMin} onMarcar={setCMin} titulo="Mínimo de noches (estadía)">
            <input type="number" min={0} disabled={!cMin} className={fieldCls} placeholder="Sin mínimo" value={minEst} onChange={(e) => setMinEst(e.target.value)} />
          </FilaCampo>
          <FilaCampo marcar={cMax} onMarcar={setCMax} titulo="Máximo de noches (estadía)">
            <input type="number" min={0} disabled={!cMax} className={fieldCls} placeholder="Sin máximo" value={maxEst} onChange={(e) => setMaxEst(e.target.value)} />
          </FilaCampo>
          <FilaCampo marcar={cCta} onMarcar={setCCta} titulo="Cerrado a llegadas (CTA)">
            <Switch checked={cta} onChange={setCta} disabled={!cCta} label={cta ? 'Sí' : 'No'} />
          </FilaCampo>
          <FilaCampo marcar={cCtd} onMarcar={setCCtd} titulo="Cerrado a salidas (CTD)">
            <Switch checked={ctd} onChange={setCtd} disabled={!cCtd} label={ctd ? 'Sí' : 'No'} />
          </FilaCampo>
          <FilaCampo marcar={cCerrada} onMarcar={setCCerrada} titulo="Fecha cerrada (no se vende)">
            <Switch checked={cerrada} onChange={setCerrada} disabled={!cCerrada} label={cerrada ? 'Sí' : 'No'} />
          </FilaCampo>
        </div>

        <div className="flex justify-end border-t pt-3">
          <Button isLoading={enviando} disabled={!desde || !hasta || !algoSeleccionado} onClick={aplicar}>Aplicar</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function FilaCampo({ titulo, marcar, onMarcar, children }: { titulo: string; marcar: boolean; onMarcar: (v: boolean) => void; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[auto_1fr_120px] items-center gap-2">
      <input type="checkbox" checked={marcar} onChange={(e) => onMarcar(e.target.checked)} className="h-4 w-4 rounded border-input" title="Cambiar" />
      <span className="text-xs">{titulo}</span>
      <div>{children}</div>
    </div>
  );
}
